"use client"

import { PropsWithChildren, useMemo } from "react"
import { responsiveWidthVariants } from "@/constants/tailwindClasses"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { storyblokEditable } from "@storyblok/react/rsc"

import { CustomerData } from "@/lib/intershop/interfaces/customer/customer"
import { UserData } from "@/lib/intershop/interfaces/user/user"
import { cn } from "@/lib/utils"
import { UbContextDependentStoryblok } from "@/components/storyblok/component-types"

interface UbContextDependentClientProps extends PropsWithChildren {
  blok: UbContextDependentStoryblok
  showContent: boolean
  matchAll: boolean
  isB2B: boolean
  isoLocale: string
  customer?: CustomerData
}

export default function UbContextDependentClient({
  blok,
  showContent,
  matchAll,
  children,
  isB2B,
  isoLocale,
  customer,
}: UbContextDependentClientProps) {
  const basketNotEmpty: boolean = blok.basketNotEmpty || false
  const basketEmpty: boolean = blok.basketEmpty || false
  const negation: boolean = blok.negation || false

  const basketHasInvoiceToAddress: boolean = blok.basketHasInvoiceToAddress || false
  const basketHasShipToAddress: boolean = blok.basketHasShipToAddress || false
  const basketShipToAddressSameAsInvoiceToAddress: boolean = blok.basketShipToAddressSameAsInvoiceToAddress || false
  const hasAddresses: boolean = blok.hasAddresses || false

  const { setBasketLineItems, setBasketData, basketData, basketLineItems, addresses } = useUbStorefrontState()
  /*

    useEffect(() => {
      if (basketData) {
        loadBasketLineItemsAction(isoLocale, isB2B, customer, basketData).then((lineItems) => {
          if (lineItems) {
            lineItems.sort((a, b) => (a.position > b.position ? 1 : -1))
            //console.dir(lineItems, {depth: 9})

            setBasketLineItems(lineItems)
          }
        })
      } else {
        loadCurrentBasketAction().then((loadedBasketData) => {
          if (loadedBasketData) {
            setBasketData(loadedBasketData)
          }
        })
      }
    }, [basketData, isB2B, isoLocale, setBasketData, setBasketLineItems, customer])
  */

  const hasLineItems = useMemo(() => {
    return (basketData?.data?.totalProductQuantity ?? 0) > 0 && basketLineItems && basketLineItems.length > 0
  }, [basketData?.data?.totalProductQuantity, basketLineItems])

  const hasInvoiceToAddress = useMemo(() => {
    const invoiceToAddressExists = !!basketData?.data?.invoiceToAddress
    // console.log("invoiceToAddressExists", invoiceToAddressExists)
    return invoiceToAddressExists
  }, [basketData?.data?.invoiceToAddress])

  const hasShipToAddress = useMemo(() => {
    const commonShipToAddressExists = !!basketData?.data?.commonShipToAddress
    //console.log("commonShipToAddressExists", commonShipToAddressExists)
    return commonShipToAddressExists
  }, [basketData?.data?.commonShipToAddress])

  function basketEmptyCheck() {
    return basketEmpty ? !hasLineItems : matchAll
  }

  const shipToAddressSameAsInvoiceToAddress = useMemo(() => {
    const shipToAddress = basketData?.data?.commonShipToAddress
    const invoiceToAddress = basketData?.data?.invoiceToAddress

    if (shipToAddress && invoiceToAddress) {
      if (shipToAddress === invoiceToAddress) {
        return true
      }
    }
    return false
  }, [basketData])

  function basketNotEmptyCheck() {
    return basketNotEmpty ? hasLineItems : matchAll
  }

  function basketHasInvoiceToAddressCheck() {
    return basketHasInvoiceToAddress ? hasInvoiceToAddress : matchAll
  }

  function basketHasShipToAddressCheck() {
    return basketHasShipToAddress ? hasShipToAddress : matchAll
  }

  function basketShipToAddressSameAsInvoiceToAddressCheck() {
    return basketShipToAddressSameAsInvoiceToAddress ? shipToAddressSameAsInvoiceToAddress : matchAll
  }

  function hasAddressesCheck() {
    return hasAddresses ? addresses && addresses.length > 0 : matchAll
  }

  let showContentClient = matchAll
    ? showContent &&
      basketEmptyCheck() &&
      basketNotEmptyCheck() &&
      basketHasInvoiceToAddressCheck() &&
      basketHasShipToAddressCheck() &&
      basketShipToAddressSameAsInvoiceToAddressCheck() &&
      hasAddressesCheck()
    : showContent ||
      basketEmptyCheck() ||
      basketNotEmptyCheck() ||
      basketHasInvoiceToAddressCheck() ||
      basketHasShipToAddressCheck() ||
      basketShipToAddressSameAsInvoiceToAddressCheck() ||
      hasAddressesCheck()

  //console.log(blok.name + " showContentClient: " + showContent)

  return (
    <>
      {
        //If one of the basket tests is needed then wait for basket loader
        (basketNotEmpty ||
        basketEmpty ||
        basketHasShipToAddress ||
        basketHasInvoiceToAddress ||
        basketShipToAddressSameAsInvoiceToAddress
          ? basketData && basketLineItems
          : true) &&
        (negation ? !showContentClient : showContentClient) &&
        children ? (
          <span className={cn("", responsiveWidthVariants(blok.width))} {...storyblokEditable(blok)}>
            {children}
          </span>
        ) : (
          <></>
        )
      }
    </>
  )
}
